<template>
    <div>
        <org-unit-users-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            :sortOptions="[
                {
                    value: 'id',
                    label: 'Sort: Added at',
                },
            ]"
            :roleOptions="orgUnitRolesOptions()"
            :orgUnitId="props.orgUnitId"
            :loading="loading"
            @add="showUserOrgUnitModal = true"
        >
        </org-unit-users-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <vx-box
                v-if="!isEqual(defaultFilters(), fetchParams)"
                color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                <div class="w-64 mx-auto text-center">No results for current search, try adjusting your filters.</div>
                <vx-button type="button" color="secondary" @click="resetFilters">
                    Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </vx-box>
            <vx-box
                v-else-if="!list.length"
                color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No pending users yet.
                <vx-button
                    v-if="onCorporate() || ['admin'].includes(roleOnCurrentOrgUnit)"
                    @click="showUserOrgUnitModal = true"
                >
                    Add User <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>
        </template>

        <vx-table v-if="list.length" class="xl:grid-cols-5-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>Email</div>
                <div>OU Role</div>
                <div>Status</div>
                <div>Invited At</div>
            </vx-table-row>

            <org-unit-pending-user-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                :teamId="props.teamId"
                :orgUnitId="props.orgUnitId"
                @deleted="removeFromList($event)"
            >
            </org-unit-pending-user-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
            </vx-button>
            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                {{ list.length }}/{{ props.totalCount }}
            </div>
        </div>

        <org-unit-user-modal-form
            v-model="newUser"
            v-model:visible="showUserOrgUnitModal"
            :orgUnitId="props.orgUnitId"
            :orgUnitName="props.orgUnitName"
            :teamId="props.teamId"
            @added="emit('added', $event)"
            @updated="emit('updated', $event)"
            @invited="
                () => {
                    emit('invited');
                    loadData();
                }
            "
        ></org-unit-user-modal-form>
    </div>
</template>
<script setup>
import PageLoading from '~/components/general/PageLoading.vue';
import OrgUnitPendingUserRow from './OrgUnitPendingUserRow.vue';
import OrgUnitUsersControls from './OrgUnitUsersControls.vue';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faCircleX, faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { onMounted, ref } from 'vue';
import { isEqual } from '~/utils/helpers';
import { orgUnitInvitationsClient } from '~/services';
import { DEFAULT_ORG_UNIT_ROLE, orgUnitRolesOptions } from '~/constants/roles';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import OrgUnitUserModalForm from '~/components/settings/teams/org-units/form/users/OrgUnitUserModalForm.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
    orgUnitName: {
        type: String,
        required: false,
    },
    totalCount: {
        type: Number,
        default: 0,
    },
});
const emit = defineEmits(['added', 'updated', 'deleted', 'invited']);

const showUserOrgUnitModal = ref(false);
const newUser = ref({
    id: null,
    name: '',
    email: '',
    role: DEFAULT_ORG_UNIT_ROLE,
});

const defaultFilters = () => ({
    cursor: null,
    sort_dir: 'desc',
    sort_field: 'id',
    per_page: 10,
    role: null,
    query: null,
});

const { onCorporate, roleOnCurrentOrgUnit } = useCurrentGroup();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

const loadData = () => {
    fetchRecords(orgUnitInvitationsClient.listInvitations(props.teamId, props.orgUnitId, { ...fetchParams.value }));
};

const removeFromList = (itemId) => {
    emit('deleted', itemId);
    list.value = list.value.filter((item) => item.id != itemId);

    if (!list.value.length) {
        loadData();
    }
};

onMounted(() => {
    resetFilters();
});

defineExpose({
    loadData,
});
</script>
